
import Page from '@/Page.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';

import {
    HourlyPlanTypeEnum,
    LocationModel,
    PlanItemModel,
    QueryOperator,
    ShiftModel,
    ShiftProductionRecordModel,
    ShiftScrapRecordModel,
    SortOrder,
    WorkplaceModel,
} from '@/libs/Api';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { locationsStore } from '@/libs/locations/+state/store';
import { shiftScrapRecordsStore, shiftsStore } from '@/libs/shifts/+state/store';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import EditShiftDetailDialog from '@/libs/shifts/components/EditShiftDetailDialog.vue';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import ShiftDetailDialog from '@/libs/shifts/components/ShiftDetailDialog.vue';
import {
    getDayStartMinutes,
    startOfProductionDayMs,
    endOfProductionDayMs,
    startOfProductionDayIso,
    endOfProductionDayIso,
} from '@/libs/tpm-analytics/core/production-day.utils';

@Component({
    components: { ShiftDetailDialog, EditShiftDetailDialog },
})
export default class ShiftReportLocation extends Page {
    @Prop() locationId!: string | null;

    protected date: Date = new Date();
    protected dateString = '';
    protected activeShiftId: string | null = null;

    protected daysBefore = 6;
    protected daysAfter = 0;

    protected menuDesktop = false;
    protected menuMobile = false;
    protected showHourlyDetails = false;
    protected expandedWorkplaces: { [key: string]: boolean } = {};
    
    private isUpdatingDate = false; // Flag pro prevenci cyklického volání

    private readonly HOURLY_DETAILS_STORAGE_KEY = 'shift-report-show-hourly-details';

    protected headers = [{ text: this.$t('common.workplace'), value: 'name', sortable: false, align: 'left' }];

    get isValidDate(): boolean {
        return this.date && moment(this.date).isValid();
    }

    get isLoading(): boolean {
        return (
            shiftsStore.useGetter(CrudGetter.Pending, 'ShiftReportLocation') ||
            shiftScrapRecordsStore.useGetter(CrudGetter.Pending, 'ShiftReportLocation') ||
            planItemsStore.useGetter(CrudGetter.Pending, 'ShiftReportLocation')
        );
    }

    get hours(): {
        fromTime: number | null;
        fromTimeString: string | null;
        toTime: number | null;
        toTimeString: string | null;
        lines: {
            workplace: WorkplaceModel;
            okCount: number;
            nokCount: number;
            plan: number;
            shutdownDots: { isPlanned: boolean }[];
            shiftId: string | null;
        }[];
        plan: number;
        okCount: number;
        nokCount: number;
        isFinal: boolean;
    }[] {
        // Použijeme začátek výrobního dne podle dayStart z tenanta
        const fromTimeIso = startOfProductionDayIso(this.date);
        const toTimeIso = endOfProductionDayIso(this.date);
        let fromTime = moment.tz(fromTimeIso, DISPLAY_TIMEZONE).toDate();
        const toTime = moment.tz(toTimeIso, DISPLAY_TIMEZONE).toDate();
        const items: {
            fromTime: number | null;
            fromTimeString: string | null;
            toTime: number | null;
            toTimeString: string | null;
            lines: {
                workplace: WorkplaceModel;
                okCount: number;
                nokCount: number;
                plan: number;
                shutdownDots: { isPlanned: boolean }[];
                shiftId: string | null;
            }[];
            plan: number;
            okCount: number;
            nokCount: number;
            isFinal: boolean;
        }[] = [];
        const shifts: ShiftModel[] = this.getShifts();
        const transformedShifts = shifts.map((e) => {
            return {
                id: e.id,
                fromTime: moment.tz(e.fromDate!, DISPLAY_TIMEZONE).valueOf(),
                toTime: moment.tz(e.toDate!, DISPLAY_TIMEZONE).valueOf(),
                hourlyPlan: this.getShiftHourlyPlan(e),
                workplaceId: e.workplaceId!,
            };
        });
        const shutdowns = ((planItemsStore.useGetter(CrudGetter.Data, 'ShiftReportLocation') ?? []) as PlanItemModel[])
            .map((x) => {
                return {
                    fromTime: x.shutdownFromDate ? moment.tz(x.shutdownFromDate!, DISPLAY_TIMEZONE).valueOf() : moment.tz(DISPLAY_TIMEZONE).valueOf(),
                    toTime: x.shutdownToDate ? moment.tz(x.shutdownToDate!, DISPLAY_TIMEZONE).valueOf() : moment.tz(DISPLAY_TIMEZONE).valueOf(),
                    workplaceId: x.workplaceId!,
                    isPlanned: x.auditFaultType?.isPlannedShutdown ?? false,
                };
            })
            .filter((x) => x.fromTime < x.toTime)
            .sort((a, b) => a.fromTime - b.fromTime) // Sort shutdowns by fromTime
            .reduce((acc, current) => {
                if (acc.filter((x) => x.workplaceId == current.workplaceId).length === 0) {
                    // If no shutdown in the accumulator, add the current one
                    acc.push(current);
                } else {
                    // Get the last shutdown in the accumulator
                    const last = acc.filter((x) => x.workplaceId == current.workplaceId)[
                    acc.filter((x) => x.workplaceId == current.workplaceId).length - 1
                        ];

                    // Check if the current shutdown overlaps or is adjacent to the last one
                    if (current.fromTime <= last.toTime) {
                        // Merge the two shutdowns by extending the toTime of the last shutdown
                        last.toTime = Math.max(last.toTime, current.toTime);
                    } else {
                        // No overlap, add the current shutdown to the accumulator
                        acc.push(current);
                    }
                }
                return acc;
            }, [] as { fromTime: number; toTime: number; workplaceId: string; isPlanned: boolean }[]);
        const scrapRecords: ShiftScrapRecordModel[] = this.getScrapRecords();
        const workplaceRecords = shifts.reduce(
            (acc, e) => {
                const productions = (e.shiftProductions ?? [])
                    .reduce((acc, n) => {
                        return [...acc, ...(n.shiftProductionRecords ?? [])];
                    }, [] as ShiftProductionRecordModel[])
                    .map((n: ShiftProductionRecordModel) => {
                        return {
                            recordTime: moment.tz(n.recordTime!, DISPLAY_TIMEZONE).valueOf(),
                            okCount: n.okCount ?? 0,
                        };
                    });
                if (Object.keys(acc).contains(e.workplaceId!)) {
                    acc[e.workplaceId!] = [...acc[e.workplaceId!], ...productions];
                } else {
                    acc[e.workplaceId!] = productions;
                }
                return acc;
            },
            {} as {
                [key: string]: { recordTime: number; okCount: number }[];
            }
        );
        const workplaceScrapRecords = shifts.reduce(
            (acc, e) => {
                const records = scrapRecords
                    .filter((x) => x.shiftId == e.id)
                    .map((n: ShiftScrapRecordModel) => {
                        return {
                            recordTime: moment.tz(n.recordTime!, DISPLAY_TIMEZONE).valueOf(),
                            scrapCount: n.scrapCount ?? 0,
                        };
                    });
                if (records.length == 0) {
                    return acc;
                }
                if (Object.keys(acc).contains(e.workplaceId!)) {
                    acc[e.workplaceId!] = [...acc[e.workplaceId!], ...records];
                } else {
                    acc[e.workplaceId!] = records;
                }
                return acc;
            },
            {} as {
                [key: string]: { recordTime: number; scrapCount: number }[];
            }
        );
        const workplaces = this.getWorkplaces();
        while (fromTime.getTime() < toTime.getTime()) {
            const fromTimeNumber = fromTime.getTime();
            const toTimeNumber = fromTime.getTime() + 3600000;
            const lines = workplaces.map((e) => {
                const records = workplaceRecords[e.id!] ?? [];
                const scrapRecords = workplaceScrapRecords[e.id!] ?? [];
                const lineShifts = transformedShifts.filter(
                    (x) => x.workplaceId == e.id! && x.fromTime < toTimeNumber && x.toTime > fromTimeNumber
                );
                return {
                    workplace: e,
                    okCount: records
                        .filter((x) => x.recordTime > fromTimeNumber && x.recordTime <= toTimeNumber)
                        .reduce((acc, e) => acc + e.okCount, 0),
                    nokCount: scrapRecords
                        .filter((x) => x.recordTime > fromTimeNumber && x.recordTime <= toTimeNumber)
                        .reduce((acc, e) => acc + e.scrapCount, 0),
                    plan: lineShifts
                        .map((x) => {
                            const pFromTime = fromTimeNumber > x.fromTime ? fromTimeNumber : x.fromTime;
                            const pToTime = toTimeNumber < x.toTime ? toTimeNumber : x.toTime;
                            if (pFromTime < pToTime) {
                                return Math.round((x.hourlyPlan ?? 0) * ((pToTime - pFromTime) / 3600000));
                            }
                            return 0;
                        })
                        .reduce((acc, x) => acc + x, 0),
                    shutdownDots: shutdowns
                        .filter((x) => x.workplaceId == e.id! && x.fromTime < toTimeNumber && x.toTime > fromTimeNumber)
                        .flatMap((x) => {
                            const pFromTime = fromTimeNumber > x.fromTime ? fromTimeNumber : x.fromTime;
                            const pToTime = toTimeNumber < x.toTime ? toTimeNumber : x.toTime;
                            if (pFromTime < pToTime) {
                                const duration = pToTime - pFromTime;
                                const dotCount = Math.ceil(duration / 600000);
                                return Array(dotCount).fill({ isPlanned: x.isPlanned });
                            }
                            return [];
                        }),
                    shiftId: lineShifts.length > 0 ? lineShifts[0].id! : null,
                };
            });
            items.push({
                fromTime: fromTimeNumber,
                fromTimeString: fromTime.toISOString(),
                toTime: toTimeNumber,
                toTimeString: moment.tz(toTimeNumber, DISPLAY_TIMEZONE).toISOString(),
                lines,
                plan: lines.reduce((acc, e) => acc + e.plan, 0),
                okCount: lines.reduce((acc, e) => acc + e.okCount, 0),
                nokCount: lines.reduce((acc, e) => acc + e.nokCount, 0),
                isFinal: false,
            });
            fromTime = moment(fromTime).add(1, 'hour').toDate();
        }
        const totalLines = workplaces.map((e) => {
            const records = workplaceRecords[e.id!] ?? [];
            const scrapRecords = workplaceScrapRecords[e.id!] ?? [];
            const workplaceShifts = transformedShifts.filter((x) => x.workplaceId == e.id);
            const totalPlan = workplaceShifts.reduce((acc, shift) => {
                // Vypočítat délku směny v hodinách a vynásobit hodinovým plánem
                const shiftDurationHours = (shift.toTime - shift.fromTime) / 3600000;
                return acc + Math.round(shift.hourlyPlan * shiftDurationHours);
            }, 0);
            return {
                workplace: e,
                okCount: records.reduce((acc, e) => acc + e.okCount, 0),
                nokCount: scrapRecords.reduce((acc, e) => acc + e.scrapCount, 0),
                plan: totalPlan,
                shutdownDots: [],
                shiftId: null,
            };
        });
        items.push({
            fromTime: null,
            fromTimeString: null,
            toTime: null,
            toTimeString: null,
            lines: totalLines,
            plan: totalLines.reduce((acc, e) => acc + e.plan, 0),
            okCount: totalLines.reduce((acc, e) => acc + e.okCount, 0),
            nokCount: totalLines.reduce((acc, e) => acc + e.nokCount, 0),
            isFinal: true,
        });
        return items;
    }

    @Watch('locationId')
    locationIdChanged(v: string | null, p: string | null) {
        if (v != p) {
            this.loadShifts();
            this.loadScrapRecords();
            this.loadShutdowns();
        }
    }

    @Watch('dateString')
    dateStringChanged(v: string) {
        // Ignorovat změny způsobené programovým nastavením
        if (this.isUpdatingDate) {
            return;
        }
        
        // Ignorovat prázdné stringy a validovat formát
        if (v && v.trim() !== '' && moment(v, 'YYYY-MM-DD', true).isValid()) {
            this.setDate(moment.tz(v, DISPLAY_TIMEZONE).toDate());
        }
    }

    created() {
        this.isUpdatingDate = true;
        try {
            if (this.getQueryInt('date', true)) {
                this.date = moment.tz(this.getQueryInt('date', true)!, DISPLAY_TIMEZONE).toDate();
            } else {
                // Pokud je aktuální čas před začátkem výrobního dne, použijeme předchozí výrobní den
                const now = Date.now();
                const dayStartMinutes = getDayStartMinutes();
                const nowPrague = moment.tz(DISPLAY_TIMEZONE);
                const currentHour = nowPrague.hour();
                const currentMinute = nowPrague.minute();
                const currentMinutesFromMidnight = currentHour * 60 + currentMinute;

                /*if (currentMinutesFromMidnight < dayStartMinutes) {
                    // Jsme před začátkem výrobního dne, použijeme včerejší výrobní den
                    this.date = moment.tz(startOfProductionDayMs(now - 86400000), DISPLAY_TIMEZONE).toDate(); // -1 den v ms
                } else {
                    this.date = moment.tz(startOfProductionDayMs(now), DISPLAY_TIMEZONE).toDate();
                }*/
                this.date = moment.tz(startOfProductionDayMs(now), DISPLAY_TIMEZONE).toDate();
            }
            
            // Inicializace dateString pro synchronizaci s date
            this.dateString = moment.tz(this.date, DISPLAY_TIMEZONE).format('YYYY-MM-DD');
        } finally {
            this.isUpdatingDate = false;
        }
    }

    mounted() {
        // Načti nastavení z local storage
        const savedValue = localStorage.getItem(this.HOURLY_DETAILS_STORAGE_KEY);
        if (savedValue !== null) {
            this.showHourlyDetails = JSON.parse(savedValue);
        }

        if (this.locationId) {
            this.loadShifts();
            this.loadScrapRecords();
            this.loadShutdowns();
        }
    }

    getWorkplaces(): WorkplaceModel[] {
        const locationScopes = this.getLocationScopes();
        let workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
        workplaces = workplaces
            .filter((x) => locationScopes.includes(x.locationId!))
            .sort((a, b) => (a.name ?? '').localeCompare((b.name ?? '').toString()));
        return workplaces;
    }

    getShifts(): ShiftModel[] {
        return shiftsStore.useGetter(CrudGetter.Data, 'ShiftReportLocation') ?? [];
    }

    getScrapRecords(): ShiftModel[] {
        return shiftScrapRecordsStore.useGetter(CrudGetter.Data, 'ShiftReportLocation') ?? [];
    }

    getLocationScopes() {
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        return locations
            .filter((x) => x.locationParents!.some((y) => y.parentalLocationId! == this.locationId))
            .map((x) => x.id!)
            .concat([this.locationId ?? '']);
    }

    getDate(dayShift: number) {
        return moment.tz(DISPLAY_TIMEZONE).startOf('day').add(dayShift, 'days').toDate();
    }

    getStartOfDay(date: Date) {
        if (!date || !moment(date).isValid()) {
            return moment.tz(DISPLAY_TIMEZONE).startOf('day').toDate();
        }
        return moment.tz(date, DISPLAY_TIMEZONE).startOf('day').toDate();
    }

    isDate(e: Date, d: Date) {
        // Validace obou datumů před porovnáním
        if (!e || !moment(e).isValid() || !d || !moment(d).isValid()) {
            return false;
        }
        // Porovnání datumů v Prague timezone
        const eDate = moment.tz(e, DISPLAY_TIMEZONE).format('YYYY-MM-DD');
        const dDate = moment.tz(d, DISPLAY_TIMEZONE).format('YYYY-MM-DD');
        return eDate === dDate;
    }

    setDate(e: Date) {
        // Validace data před nastavením
        if (!e || !moment(e).isValid()) {
            console.error('Invalid date provided to setDate:', e);
            return;
        }
        
        this.isUpdatingDate = true;
        try {
            this.date = e;
            // Synchronizace dateString s date pro v-date-picker
            this.dateString = moment.tz(e, DISPLAY_TIMEZONE).format('YYYY-MM-DD');
            
            // Vypočítáme začátek následujícího výrobního dne jako expirace
            const nextDayStart = moment.tz(endOfProductionDayMs(Date.now()), DISPLAY_TIMEZONE).toDate();
            this.setQuery('date', e.getTime().toString(), true, 'Q', nextDayStart);
            this.loadShifts();
            this.loadScrapRecords();
            this.loadShutdowns();
        } finally {
            this.isUpdatingDate = false;
        }
    }

    previousDay() {
        console.log('[ShiftReport] previousDay - current date:', this.date, 'dateString:', this.dateString);
        // Validace aktuálního data před manipulací
        if (!this.date || !moment(this.date).isValid()) {
            console.error('[ShiftReport] Invalid date in previousDay:', this.date);
            this.date = moment.tz(DISPLAY_TIMEZONE).toDate(); // Fallback na dnešní datum
        }
        const newDate = moment.tz(this.date, DISPLAY_TIMEZONE).subtract(1, 'day').toDate();
        console.log('[ShiftReport] previousDay - new date:', newDate);
        this.setDate(newDate);
    }

    nextDay() {
        console.log('[ShiftReport] nextDay - current date:', this.date, 'dateString:', this.dateString);
        // Validace aktuálního data před manipulací
        if (!this.date || !moment(this.date).isValid()) {
            console.error('[ShiftReport] Invalid date in nextDay:', this.date);
            this.date = moment.tz(DISPLAY_TIMEZONE).toDate(); // Fallback na dnešní datum
        }
        const newDate = moment.tz(this.date, DISPLAY_TIMEZONE).add(1, 'day').toDate();
        console.log('[ShiftReport] nextDay - new date:', newDate);
        this.setDate(newDate);
    }

    loadShifts() {
        // Získáme začátek a konec výrobního dne jako ISO stringy s timezone offsetem
        const prodDayStartIso = startOfProductionDayIso(this.date);
        const prodDayEndIso = endOfProductionDayIso(this.date);

        shiftsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'fromDate', index: 1, order: SortOrder.Asc },
                [
                    {
                        field: 'fromDate',
                        op: QueryOperator.Lt,
                        comparand: prodDayEndIso,
                    },
                    {
                        field: 'toDate',
                        op: QueryOperator.Gt,
                        comparand: prodDayStartIso,
                    },
                    {
                        field: 'workplace.locationId',
                        op: QueryOperator.In,
                        comparand: this.getLocationScopes(),
                    },
                ],
                {
                    pageIndex: 1,
                    pageSize: 99999,
                },
                false,
                'ShiftReportLocation'
            )
        );
    }

    loadScrapRecords() {
        // Získáme začátek a konec výrobního dne jako ISO stringy s timezone offsetem
        const prodDayStartIso = startOfProductionDayIso(this.date);
        const prodDayEndIso = endOfProductionDayIso(this.date);

        shiftScrapRecordsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'id', index: 1, order: SortOrder.Asc },
                [
                    {
                        field: 'shift.fromDate',
                        op: QueryOperator.Lt,
                        comparand: prodDayEndIso,
                    },
                    {
                        field: 'shift.toDate',
                        op: QueryOperator.Gt,
                        comparand: prodDayStartIso,
                    },
                    {
                        field: 'shift.workplace.locationId',
                        op: QueryOperator.In,
                        comparand: this.getLocationScopes(),
                    },
                ],
                {
                    pageIndex: 1,
                    pageSize: 99999,
                },
                false,
                'ShiftReportLocation'
            )
        );
    }

    loadShutdowns() {
        // Získáme začátek a konec výrobního dne jako ISO stringy s timezone offsetem
        const fromDate = startOfProductionDayIso(this.date);
        const toDate = endOfProductionDayIso(this.date);
        planItemsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<PlanItemModel>(
                { field: 'time', index: 1, order: 'asc' },
                [
                    {
                        field: 'IsWorkplaceShutdown',
                        op: 'eq',
                        comparand: true,
                    },
                    {
                        field: 'id',
                        op: 'wrap',
                        comparand: [
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'shutdownToDate',
                                        op: 'gt',
                                        comparand: fromDate,
                                    },
                                    {
                                        field: 'shutdownFromDate',
                                        op: 'lt',
                                        comparand: toDate,
                                    },
                                ],
                                junction: 'or',
                            },
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'shutdownFromDate',
                                        op: 'lt',
                                        comparand: toDate,
                                    },
                                    {
                                        field: 'shutdownToDate',
                                        op: 'eq',
                                        comparand: null,
                                    },
                                ],
                                junction: 'or',
                            },
                        ],
                    },
                    {
                        field: 'workplace.locationId',
                        op: QueryOperator.In,
                        comparand: this.getLocationScopes(),
                    },
                ],
                undefined,
                false,
                'ShiftReportLocation'
            )
        );
    }

    getShiftHourlyPlan(shift: ShiftModel): number {
        if (shift.hourlyPlanType === HourlyPlanTypeEnum.ByCycleTime) {
            return shift.hourlyPlanCycleTime ?? 0;
        }
        return shift.hourlyPlan ?? 0;
    }

    openWorkplaceShiftClick(shiftId: string) {
        this.activeShiftId = shiftId;
    }

    toggleWorkplaceExpand(workplaceId: string) {
        this.$set(this.expandedWorkplaces, workplaceId, !this.expandedWorkplaces[workplaceId]);
    }

    @Watch('showHourlyDetails')
    onShowHourlyDetailsChange(newValue: boolean) {
        // Ulož do local storage
        localStorage.setItem(this.HOURLY_DETAILS_STORAGE_KEY, JSON.stringify(newValue));

        // Když se změní toggle, rozbal/sbal všechna pracoviště
        const workplaces = this.getWorkplaceData();
        workplaces.forEach((workplace) => {
            this.$set(this.expandedWorkplaces, workplace.id, newValue);
        });
    }

    getTotalSummary() {
        const finalHour = this.hours.find((h) => h.isFinal);
        return {
            plan: finalHour?.plan ?? 0,
            ok: finalHour?.okCount ?? 0,
            nok: finalHour?.nokCount ?? 0,
        };
    }

    getWorkplaceData() {
        const workplaces = this.getWorkplaces();
        const finalHour = this.hours.find((h) => h.isFinal);

        return workplaces.map((workplace) => {
            const finalLine = finalHour?.lines.find((l) => l.workplace.id === workplace.id);
            const hourlyData = this.hours
                .filter((h) => !h.isFinal)
                .map((hour) => {
                    const line = hour.lines.find((l) => l.workplace.id === workplace.id);
                    return {
                        fromTimeString: hour.fromTimeString,
                        toTimeString: hour.toTimeString,
                        plan: line?.plan ?? 0,
                        okCount: line?.okCount ?? 0,
                        nokCount: line?.nokCount ?? 0,
                        shutdownDots: line?.shutdownDots ?? [],
                        shiftId: line?.shiftId ?? null,
                    };
                });

            return {
                id: workplace.id!,
                name: workplace.name!,
                totalPlan: finalLine?.plan ?? 0,
                totalOk: finalLine?.okCount ?? 0,
                totalNok: finalLine?.nokCount ?? 0,
                hourlyData,
            };
        });
    }
}
